import type { Fraction } from './fractionType';
import type { FuzzyBounds, FuzzyValue } from './fuzzyNumbers';
import { addFractions, approximateFractionValue, approximateNumber, compareFractions, divideFractions, lessThanFraction, multiplyFractions, subtractFractions, validateFraction } from './integerMath';
import { invariant } from './invariant';
import { makeValidationFunction, validateNumberProperty, validateOptionalObjectProperty, validateOptionalStringProperty } from './validation';

/*
  Fuzzy math operates on values that can either be precise or be approximate,
  depending on the reliabiliy of its source. Operations on these values
  propagate precision and accuracy from operands to computed values.
*/

export function fractionToFuzzy(frac: Fraction, accuracyWarning?: string): FuzzyValue {
  return {
    precise: frac,
    approximate: approximateFractionValue(frac),
    accuracyWarning
  };
}


export function fuzzyToFraction(fuzzy: FuzzyValue): Fraction {
  return fuzzy.precise ?? approximateNumber(fuzzy.approximate);
}


export function integerToFuzzy(integerValue: number) {
  invariant(Number.isInteger(integerValue), 'Expected integer value');
  return fractionToFuzzy({ numerator: integerValue, denominator: 1 });
}


export function addFuzzy(a: FuzzyValue, b: FuzzyValue) {
  const accuracyWarning = a.accuracyWarning ?? b.accuracyWarning;
  if (a.precise && b.precise) {
    return fractionToFuzzy(addFractions(a.precise, b.precise), accuracyWarning);
  }
  return {
    approximate: a.approximate + b.approximate,
    accuracyWarning
  };
}


export function subtractFuzzy(a: FuzzyValue, b: FuzzyValue) {
  const accuracyWarning = a.accuracyWarning ?? b.accuracyWarning;
  if (a.precise && b.precise) {
    return fractionToFuzzy(subtractFractions(a.precise, b.precise), accuracyWarning);
  }
  return {
    approximate: a.approximate - b.approximate,
    accuracyWarning
  };
}

export function multiplyFuzzy(a: FuzzyValue, b: FuzzyValue) {
  const accuracyWarning = a.accuracyWarning ?? b.accuracyWarning;
  if (a.precise && b.precise) {
    return fractionToFuzzy(multiplyFractions(a.precise, b.precise), accuracyWarning);
  }
  return {
    approximate: a.approximate * b.approximate,
    accuracyWarning
  };
}


export function divideFuzzy(a: FuzzyValue, b: FuzzyValue) {
  const accuracyWarning = a.accuracyWarning ?? b.accuracyWarning;
  if (a.precise && b.precise) {
    return fractionToFuzzy(divideFractions(a.precise, b.precise), accuracyWarning);
  }
  return {
    approximate: a.approximate / b.approximate,
    accuracyWarning
  };
}

export function lessThanFuzzy(a: FuzzyValue, b: FuzzyValue) {
  if (a.precise && b.precise) {
    return lessThanFraction(a.precise, b.precise);
  }

  return a.approximate < b.approximate;
}

export function compareFuzzy(a: FuzzyValue, b: FuzzyValue) {
  if (a.precise && b.precise) {
    return compareFractions(a.precise, b.precise);
  }

  return a.approximate - b.approximate;
}

export function minFuzzy(a: FuzzyValue, b: FuzzyValue) {
  if ((a.precise !== undefined) && (b.precise !== undefined)) {
    return (compareFractions(b.precise, a.precise) < 0) ? b : a;
  } else {
    return (b.approximate < a.approximate) ? b : a;
  }
}

export function maxFuzzy(a: FuzzyValue, b: FuzzyValue) {
  if ((a.precise !== undefined) && (b.precise !== undefined)) {
    return (compareFractions(b.precise, a.precise) > 0) ? b : a;
  } else {
    return (b.approximate > a.approximate) ? b : a;
  }
}

export const validateFuzzyValue =
  makeValidationFunction<FuzzyValue>((u) => {
    return {
      precise: validateOptionalObjectProperty(u, 'precise', 'Fraction', validateFraction),
      approximate: validateNumberProperty(u, 'approximate'),
      accuracyWarning: validateOptionalStringProperty(u, 'accuracyWarning'),
    };
  });


export function shrinkRange(range: FuzzyBounds, shrinkBy: FuzzyValue): FuzzyBounds {
  const start = addFuzzy(range.start, shrinkBy);
  const end = subtractFuzzy(range.end, shrinkBy);
  return { start, end: maxFuzzy(end, start) };
}

export function expandRange(range: FuzzyBounds, expandBy: FuzzyValue): FuzzyBounds {
  const start = subtractFuzzy(range.start, expandBy);
  const end = addFuzzy(range.end, expandBy);
  return { start, end: maxFuzzy(end, start) };
}

export function expandStartOfRange(range: FuzzyBounds, expandBy: FuzzyValue): FuzzyBounds {
  const start = subtractFuzzy(range.start, expandBy);
  return { start, end: maxFuzzy(range.end, start) };
}

export function shrinkRanges(ranges: FuzzyBounds[], shrinkBy: FuzzyValue): FuzzyBounds[] {
  return ranges.map((range) => shrinkRange(range, shrinkBy));
}

export function expandStartOfRanges(ranges: FuzzyBounds[], expandBy: FuzzyValue): FuzzyBounds[] {
  return ranges.map((range) => expandStartOfRange(range, expandBy));
}

/**
 * Checks if the provided range is entirely covered by the list of ranges in
 * the "coverage" parameter. Note the behavior for this function if range.start
 * > range.end is that you'll instead check if any part of the range [end, start>
 * is covered by the ranges in the "coverage" parameter.
 */
export function isRangeCovered(coverage: FuzzyBounds[], range: FuzzyBounds): boolean {
  for (const bufferedRange of coverage) {
    if (compareFuzzy(range.start, bufferedRange.start) < 0) {
      continue;
    }
    if (compareFuzzy(range.end, bufferedRange.end) > 0) {
      continue;
    }
    return true;
  }
  return false;
}

/** Returns the new list of ranges after appending the range parameter to the
 * list of ranges. Returns a sorted list of ranges with any overlapping ranges
 * merged into one continuous range.
 */
export function appendRange(ranges: FuzzyBounds[], range: FuzzyBounds): FuzzyBounds[] {
  const oldRanges : FuzzyBounds[] = [...ranges, range];
  oldRanges.sort((a, b) => compareFuzzy(a.start, b.start));
  const newRanges : FuzzyBounds[] = [];
  let currentRange : FuzzyBounds | null = null;
  for (const range of oldRanges) {
    if (currentRange !== null) {
      if (compareFuzzy(range.start, currentRange.end) <= 0) {
        currentRange.end = maxFuzzy(range.end, currentRange.end);
        continue;
      }
      newRanges.push(currentRange);
      currentRange = null;
    }

    currentRange = range;
  }
  if (currentRange !== null) {
    newRanges.push(currentRange);
  }

  return newRanges;
}

/** Assuming the input ranges are a sorted list of non-overlapping FuzzyBounds,
 *  returns a new list of ranges after removing the interval specified by the range
 *  parameter.
 */
export function removeRange(ranges: FuzzyBounds[], range: FuzzyBounds): FuzzyBounds[] {
  // Empty range to delete
  if (compareFuzzy(range.end, range.start) <= 0) {
    return [...ranges];
  }

  const newRanges : FuzzyBounds[] = [];
  for (const r of ranges) {
    if (compareFuzzy(range.end, r.start) <= 0) {
      // entirely before
      newRanges.push(r);
    } else if (compareFuzzy(range.start, r.start) <= 0) {
      if (compareFuzzy(range.end, r.end) >= 0) {
        // remove entire range
      } else {
        // remove start of range
        newRanges.push({ start: range.end, end: r.end });
      }
    } else if (compareFuzzy(range.end, r.end) < 0) {
      // remove middle of range (split in two)
      newRanges.push({ start: r.start, end: range.start });
      newRanges.push({ start: range.end, end: r.end });
    } else if (compareFuzzy(range.start, r.end) < 0) {
      // remove end of range
      newRanges.push({ start: r.start, end: range.start });
    } else {
      // no overlap
      newRanges.push(r);
    }
  }

  return newRanges;
}

/**
 * Given multiple lists of fuzzy bounds return a flat list of FuzzyBounds
 * where all lists overlap.
 */
export function rangesIntersection(listOfListOfRanges: FuzzyBounds[][]): FuzzyBounds[] {
  const deltaOverlapCounts : { offset: FuzzyValue, delta: number }[] = [];
  for (const ranges of listOfListOfRanges) {
    for (const range of ranges) {
      deltaOverlapCounts.push({ offset: range.start, delta: 1 });
      deltaOverlapCounts.push({ offset: range.end, delta: -1 });
    }
  }

  deltaOverlapCounts.sort((a, b) => compareFuzzy(a.offset, b.offset));

  const outRanges : FuzzyBounds[] = [];
  let currentOverlap = 0;
  let currentRangeStart : FuzzyValue | null = null;
  for (const entry of deltaOverlapCounts) {
    currentOverlap += entry.delta;
    if ((currentRangeStart === null) && (currentOverlap === listOfListOfRanges.length)) {
      currentRangeStart = entry.offset;
    }
    if ((currentRangeStart !== null) && currentOverlap !== listOfListOfRanges.length) {
      outRanges.push({ start: currentRangeStart, end: entry.offset });
      currentRangeStart = null;
    }
  }
  return outRanges;
}

export function humanReadableRange(range: FuzzyBounds, numberOfDigits: number = 2): string {
  return `[${range.start.approximate.toFixed(numberOfDigits)}, ${range.end.approximate.toFixed(numberOfDigits)}>`;
}

export function humanReadableRanges(ranges: FuzzyBounds[], numberOfDigits: number = 2): string {
  return ranges.map((range) => humanReadableRange(range, numberOfDigits)).join(', ');
}

